import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n";

const STORAGE_KEY = "epa_cookie_consent_v1";

type Prefs = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  decidedAt: string;
};

export function CookieConsent() {
  const { t } = useTranslation("marketing");
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [open, setOpen] = useState(false);
  const [customise, setCustomise] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Prefs;
        setPrefs(parsed);
        setAnalytics(parsed.analytics);
        setMarketing(parsed.marketing);
      } else {
        setOpen(true);
      }
    } catch {
      setOpen(true);
    }
  }, []);

  function save(next: Omit<Prefs, "necessary" | "decidedAt">) {
    const value: Prefs = { necessary: true, ...next, decidedAt: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    setPrefs(value);
    setOpen(false);
    setCustomise(false);
    // Broadcast so integrations can react.
    window.dispatchEvent(new CustomEvent("epa:cookie-consent", { detail: value }));
  }

  // Expose a small opener so the footer link can reopen the banner.
  useEffect(() => {
    (window as unknown as { openCookieSettings?: () => void }).openCookieSettings = () => {
      setCustomise(true);
      setOpen(true);
    };
  }, []);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("cookieConsent.ariaLabel", "Cookie consent")}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-gold/30 bg-background/95 backdrop-blur"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-widest">
              <Cookie className="h-3.5 w-3.5" /> {t("cookieConsent.heading", "Cookies & privacy")}
            </div>
            <p className="mt-2 text-sm text-foreground/90">
              {t("cookieConsent.body", "We use strictly necessary cookies to run the site (authentication, security, checkout). With your consent we also use optional analytics and marketing cookies to improve the auctions and personalise offers. You can change your mind at any time via the footer.")}{" "}
              <Link to="/legal/cookies" className="text-gold hover:underline">{t("cookieConsent.readPolicy", "Read our Cookie Policy")}</Link>.
            </p>

            {customise && (
              <div className="mt-4 grid gap-2 sm:grid-cols-3 text-xs">
                <div className="rounded-lg border border-border/60 bg-card/60 p-3">
                  <p className="font-semibold text-foreground">{t("cookieConsent.necessaryTitle", "Strictly necessary")}</p>
                  <p className="text-muted-foreground mt-1">{t("cookieConsent.necessaryBody", "Always on — required for login, security and checkout.")}</p>
                </div>
                <label className="rounded-lg border border-border/60 bg-card/60 p-3 cursor-pointer flex items-start gap-2">
                  <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="mt-0.5 accent-[color:var(--gold,#d4af37)]" />
                  <span>
                    <span className="block font-semibold text-foreground">{t("cookieConsent.analyticsTitle", "Analytics")}</span>
                    <span className="block text-muted-foreground mt-1">{t("cookieConsent.analyticsBody", "Aggregate usage stats to improve the platform.")}</span>
                  </span>
                </label>
                <label className="rounded-lg border border-border/60 bg-card/60 p-3 cursor-pointer flex items-start gap-2">
                  <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="mt-0.5 accent-[color:var(--gold,#d4af37)]" />
                  <span>
                    <span className="block font-semibold text-foreground">{t("cookieConsent.marketingTitle", "Marketing")}</span>
                    <span className="block text-muted-foreground mt-1">{t("cookieConsent.marketingBody", "Personalise offers and measure campaigns.")}</span>
                  </span>
                </label>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 md:flex-col md:w-56">
            <Button className="bg-gold-gradient hover:opacity-90" onClick={() => save({ analytics: true, marketing: true })}>
              {t("cookieConsent.acceptAll", "Accept all")}
            </Button>
            {customise ? (
              <Button variant="outline" onClick={() => save({ analytics, marketing })}>
                {t("cookieConsent.savePreferences", "Save preferences")}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setCustomise(true)}>
                {t("cookieConsent.customise", "Customise")}
              </Button>
            )}
            <Button variant="ghost" onClick={() => save({ analytics: false, marketing: false })}>
              {t("cookieConsent.rejectNonEssential", "Reject non-essential")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function getCookieConsent(): Prefs | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Prefs) : null;
  } catch {
    return null;
  }
}
